import React from 'react'
import styled from 'styled-components'
// import { useLocation } from 'react-router-dom'
import { useLocation, Link as RouterLink } from 'react-router-dom'

import DocumentTitle from '../../DocumentTitle'
import Link from '../../Link'
import Searchbox from '../../Searchbox'

import IPNResources from './IPNResources'
import IPNDownloads from './IPNDownloads'

import IPNPopulation from './IPNPopulation'
import IPNTandemRepeats from './IPNTandemRepeats'
import IPNVariants from './IPNVariants'
import IPNStructuralVariants from './IPNStructuralVariants'
import IPNGeneRegionSearch from './IPNGeneRegionSearch'

import {
  MediaContainer,
  CohortImage,
} from '../CohortMedia'

import populationPlot from '../Images/IMAGE_PD_PLOT.png'
import tandemRepeatsPlot from '../Images/IMAGE_TR_PLOT.png'
import snvPlot from '../Images/IMAGE_SNV_PLOT.png'
import structuralVariantsPlot from '../Images/IMAGE_SV_PLOT.png'

import ipnLogo from '../Images/LOGO_IPN.png'

const GREEN = '#00552e'
const TEXT = '#111820'
const MUTED = '#4b5563'

const Page = styled.div`
  min-height: 100vh;
  background: #fff;
  color: ${TEXT};
  font-family: Arial, Helvetica, sans-serif;
`

const Main = styled.main`
  width: calc(100% - 72px);
  margin: 0 auto;
  padding: 34px 0 48px;

  @media (max-width: 700px) {
    width: calc(100% - 32px);
  }
`

const Breadcrumbs = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 34px;
  font-size: 16px;

  a {
    color: #26384a;
    text-decoration: underline;
  }

  span {
    color: #7b8794;
  }
`

const Hero = styled.section`
  display: grid;
  grid-template-columns:
    clamp(170px, 12vw, 240px)
    minmax(500px, 1fr)
    clamp(360px, 25vw, 500px);

  gap: clamp(30px, 3vw, 60px);
  align-items: stretch;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 170px 1fr;

    > aside {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`
const IPNBadge = styled.div`
  width: 100%;
  min-height: clamp(175px, 13vw, 240px);

  border-radius: 12px;
  background: linear-gradient(145deg, #086738, #004d2b);

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: clamp(100px, 9vw, 165px);
    max-height: 80%;
    object-fit: contain;
  }
`
// const IPNBadge = styled.div`
//   width: 100%;
//   min-height: clamp(175px, 13vw, 240px);

//   border-radius: 12px;
//   background: linear-gradient(145deg, #086738, #004d2b);
//   color: white;

//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   justify-content: center;

//   strong {
//     font-size: clamp(54px, 4vw, 72px);
//     line-height: 1;
//   }

//   div {
//     font-size: clamp(48px, 3.5vw, 68px);
//     margin-top: 20px;
//   }
// `

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h1 {
    margin: 5px 0 18px;
    font-size: clamp(32px, 2.7vw, 50px);
    line-height: 1.1;
  }

  > p {
    width: 100%;
    max-width: none;
    margin: 0;
    color: ${MUTED};
    font-size: clamp(16px, 1.15vw, 20px);
    line-height: 1.6;
  }
`

const Stats = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 30px;
`

const Stat = styled.div`
  flex: 1;
  min-width: 100px;
  padding: 0 clamp(12px, 1.5vw, 28px);
  border-left: 1px solid #d9dee3;

  &:first-child {
    padding-left: 0;
    border-left: 0;
  }

  strong {
    display: block;
    font-size: clamp(20px, 1.5vw, 25px);
  }

  span {
    display: block;
    margin-top: 5px;
    color: ${MUTED};
    font-size: clamp(14px, 1vw, 16px);
  }
`

const OutlineButton = styled(Link)`
  align-self: center;
  margin-left: 20px;
  padding: 9px 16px;
  border: 1px solid ${GREEN};
  border-radius: 4px;
  color: ${GREEN};
  text-decoration: none;
`

const About = styled.aside`
  padding: clamp(24px, 2vw, 34px);
  border: 1px solid #d8dde1;
  border-radius: 12px;

  h3 {
    margin: 0 0 15px;
    font-size: clamp(20px, 1.5vw, 25px);
  }

  p {
    margin: 0 0 20px;
    line-height: 1.6;
    color: #30363d;
    font-size: clamp(16px, 1.2vw, 19px);
  }

  a {
    color: ${GREEN};
    text-decoration: none;
    font-weight: 600;
  }
`

/* =========================================================
   TABS
   ========================================================= */

const Tabs = styled.nav`
  display: flex;
  gap: 34px;
  margin-top: 38px;
  border-bottom: 1px solid #d8dde1;
  overflow-x: auto;
`

const TabLink = styled(RouterLink)`
  flex: 0 0 auto;
  padding: 14px 4px;

  color: ${({ $active }) => ($active ? GREEN : '#20262d')};

  text-decoration: none;

  font-weight: ${({ $active }) =>
    $active ? '700' : '400'};

  border-bottom: ${({ $active }) =>
    $active
      ? `2px solid ${GREEN}`
      : '2px solid transparent'};

  &:hover {
    color: ${GREEN};
  }
`

/* =========================================================
   OVERVIEW
   ========================================================= */

const SectionHeading = styled.div`
  margin: 30px 0 20px;

  h2 {
    margin: 0 0 4px;
    font-size: 23px;
  }

  p {
    margin: 0;
    color: ${MUTED};
  }
`
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(200px, 1fr));
  grid-auto-rows: 1fr;
  gap: clamp(14px, 1.3vw, 22px);

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`
// const Cards = styled.div`
//   display: grid;
//   grid-template-columns: repeat(5, minmax(200px, 1fr));
//   gap: 20px;

//   @media (max-width: 1200px) {
//     grid-template-columns: repeat(2, 1fr);
//   }

//   @media (max-width: 650px) {
//     grid-template-columns: 1fr;
//   }
// `

const Card = styled.article`
  width: 100%;
  height: clamp(360px, 29vw, 500px);
  box-sizing: border-box;

  padding: clamp(18px, 1.4vw, 26px)
           clamp(14px, 1.1vw, 20px)
           clamp(14px, 1vw, 18px);

  border: 1px solid #d8dde1;
  border-radius: 12px;

  display: flex;
  flex-direction: column;

  h3 {
    margin: 0 0 8px;
    color: ${GREEN};
    font-size: clamp(17px, 1.25vw, 21px);
  }

  p {
    margin: 0;
    color: #30363d;
    line-height: 1.5;
    font-size: clamp(14px, 1vw, 17px);
  }

  @media (max-width: 1200px) {
    height: clamp(380px, 45vw, 500px);
  }

  @media (max-width: 650px) {
    height: clamp(390px, 115vw, 520px);
  }
`

// const Card = styled.article`
//   min-height: 250px;
//   padding: 24px 18px 16px;
//   border: 1px solid #d8dde1;
//   border-radius: 12px;
//   display: flex;
//   flex-direction: column;

//   h3 {
//     margin: 0 0 8px;
//     color: ${GREEN};
//     font-size: 20px;
//   }

//   p {
//     margin: 0;
//     color: #30363d;
//     line-height: 1.5;
//   }
// `

const CardIcon = styled.div`
  color: ${GREEN};
  font-size: 34px;
  margin-bottom: 15px;
`




// const SearchPreview = styled.div`
//   flex: 1;
//   display: flex;
//   align-items: center;
// `
const SearchPreview = styled.div`
  flex: 1 1 0;
  width: 100%;
  min-height: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  margin: 18px 0;
`
const Action = styled(Link)`
  flex: 0 0 clamp(52px, 4.2vw, 68px);

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  box-sizing: border-box;

  padding: 10px 12px;

  border-radius: 6px;
  background: ${GREEN};
  color: white;

  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: clamp(14px, 1vw, 17px);
`
// const Action = styled(Link)`
//   display: block;
//   padding: 10px 12px;
//   border-radius: 4px;
//   background: ${GREEN};
//   color: white;
//   text-align: center;
//   text-decoration: none;
//   font-weight: 600;
// `

const Summary = styled.section`
  display: grid;
  grid-template-columns: 1.1fr .8fr .8fr .9fr;
  margin-top: 28px;
  border: 1px solid #d8dde1;
  border-radius: 12px;
  overflow: hidden;

  > div {
    padding: 24px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const SummaryStats = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 28px;

  strong {
    display: block;
    color: ${GREEN};
    font-size: 24px;
  }

  span {
    color: ${MUTED};
    font-size: 14px;
  }
`

const MiniList = styled.ul`
  padding: 0;
  margin: 20px 0 0;
  list-style: none;

  li {
    margin: 10px 0;
    color: #444;
  }

  li::before {
    content: '✓';
    color: ${GREEN};
    font-weight: bold;
    margin-right: 9px;
  }
`

const Help = styled.div`
  background: #f1f8f4;

  a {
    color: ${GREEN};
  }
`

/* =========================================================
   OVERVIEW CONTENT
   ========================================================= */

const IPNOverview = () => (
  <>
    <SectionHeading>
      <h2>Explore IPN Data</h2>
      <p>Dive into key data types and visualizations</p>
    </SectionHeading>

    <Cards>
      <Card>
        <CardIcon>♟</CardIcon>

        <h3>Population Data</h3>

        <p>
          Explore population structure, ancestry,
          demographics, and covariates.
        </p>

        <MediaContainer>
          <CohortImage
            src={populationPlot}
            alt="Population data visualization"
            $height="100%"
            $width="calc(100% + 36px)"
            $margin="0 -18px"
          />
        </MediaContainer>

        <Action
          preserveSelectedDataset={false}
          to="/cohort/IPN/population"
        >
          Explore Population Data →
        </Action>
      </Card>

      <Card>
        <CardIcon>≋</CardIcon>

        <h3>Tandem Repeats</h3>

        <p>
          Browse and analyze tandem repeat expansions
          across the IPN cohort.
        </p>

        <MediaContainer>
          <CohortImage
            src={tandemRepeatsPlot}
            alt="Tandem repeat visualization"
            $height="100%"
            $width="calc(100% + 36px)"
            $margin="0 -18px"
          />
        </MediaContainer>

        <Action
          preserveSelectedDataset={false}
          to="/cohort/IPN/tandem-repeats"
        >
          Explore Tandem Repeats →
        </Action>
      </Card>

      <Card>
        <CardIcon>⌬</CardIcon>

        <h3>SNVs / Small Variants</h3>

        <p>
          Search, filter, and visualize SNVs and small indels.
        </p>

        <MediaContainer>
          <CohortImage
            src={snvPlot}
            alt="SNV and small variant visualization"
            $height="100%"
            $width="calc(100% + 36px)"
            $margin="0 -18px"
          />
        </MediaContainer>

        <Action
          preserveSelectedDataset={false}
          to="/cohort/IPN/variants"
        >
          Explore SNVs →
        </Action>
      </Card>

      <Card>
        <CardIcon>▤</CardIcon>

        <h3>Structural Variants</h3>

        <p>
          Investigate structural variation across the IPN cohort.
        </p>
        <MediaContainer>
          <CohortImage
            src={structuralVariantsPlot}
            alt="Structural variant visualization"

            $height="100%"
            $width="calc(100% + 36px)"
            $margin="0 -18px"
          />
        </MediaContainer>

        <Action
          preserveSelectedDataset={false}
          to="/cohort/IPN/structural-variants"
        >
          Explore Structural Variants →
        </Action>
      </Card>

      <Card>
        <CardIcon>⌕</CardIcon>

        <h3>Gene / Region Search</h3>

        <p>
          Search genes or genomic regions across IPN data types.
        </p>

        <SearchPreview>
          <Searchbox
            width="100%"
            fixedDataset="IPN"
            hideDatasetSelector
          />
        </SearchPreview>

        <Action
          preserveSelectedDataset={false}
          to="/cohort/IPN/gene-region-search"
        >
          Explore Gene / Region Search →
        </Action>
      </Card>
    </Cards>

    <Summary>
      <div>
        <h3>Cohort Summary</h3>

        <SummaryStats>
          <div>
            <strong>788</strong>
            <span>Samples</span>
          </div>

          <div>
            <strong>421</strong>
            <span>Cases</span>
          </div>

          <div>
            <strong>367</strong>
            <span>Controls</span>
          </div>

          <div>
            <strong>~90x</strong>
            <span>Median Coverage</span>
          </div>
        </SummaryStats>
      </div>

      <div>
        <h4>Phenotype Highlights</h4>

        <MiniList>
          <li>Demographics</li>
          <li>Clinical Scores</li>
          <li>Neuropathy Subtypes</li>
          <li>Family History</li>
        </MiniList>
      </div>

      <div>
        <h4>Data Available</h4>

        <MiniList>
          <li>WGS (Illumina)</li>
          <li>Tandem Repeat Calls</li>
          <li>SNV / Indel Calls</li>
          <li>Structural Variant Calls</li>
        </MiniList>
      </div>

      <Help>
        <h4>Need Help?</h4>

        <p>
          Contact us for questions about the IPN dataset
          or access to specific data.
        </p>

        <a href="mailto:">
          Contact Support
        </a>
      </Help>
    </Summary>
  </>
)

/* =========================================================
   MAIN IPN PAGE
   ========================================================= */

const IPNPage = () => {
  const location = useLocation()
  const path = location.pathname

  /*
   * Everything above renderContent() stays on every IPN page.
   *
   * Only the section BELOW the tabs changes.
   */
  const renderContent = () => {
    switch (path) {
      case '/cohort/IPN/population':
        return <IPNPopulation />

      case '/cohort/IPN/tandem-repeats':
        return <IPNTandemRepeats />

      case '/cohort/IPN/variants':
        return <IPNVariants />

      case '/cohort/IPN/structural-variants':
        return <IPNStructuralVariants />

      case '/cohort/IPN/gene-region-search':
        return <IPNGeneRegionSearch />

      case '/cohort/IPN/downloads':
        return <IPNDownloads />

      case '/cohort/IPN/resources':
        return <IPNResources />

      default:
        return <IPNOverview />
    }
  }
  return (
    <Page>
      <DocumentTitle
        title="Idiopathic Peripheral Neuropathy (IPN)"
      />

      <Main>

        {/* =================================================
            BREADCRUMBS
            ================================================= */}

        <Breadcrumbs>
          <Link
            preserveSelectedDataset={false}
            to="/"
          >
            Home
          </Link>

          <span>›</span>

          <Link
            preserveSelectedDataset={false}
            to="/cohorts"
          >
            Explore by Cohort
          </Link>

          <span>›</span>

          <b>IPN</b>
        </Breadcrumbs>

        {/* =================================================
            SHARED HERO
            ================================================= */}

        <Hero>
          {/* <IPNBadge>
            <strong>IPN</strong>
            <div>⌁</div>
          </IPNBadge> */}
          <IPNBadge>
            <img src={ipnLogo} alt="IPN logo" />
          </IPNBadge>

          <HeroCopy>
            <h1>
              Idiopathic Peripheral Neuropathy (IPN)
            </h1>

            <p>
              Explore genomic variation, tandem repeats,
              and population-level datasets for the IPN cohort.
              Integrative analyses of SNVs, structural variants,
              repeat expansions, and more.
            </p>

            <Stats>
              <Stat>
                <strong>788</strong>
                <span>Samples</span>
              </Stat>

              <Stat>
                <strong>WGS</strong>
                <span>Data Type</span>
              </Stat>

              <Stat>
                <strong>~90x</strong>
                <span>Median Coverage</span>
              </Stat>

              <Stat>
                <strong>GRCh38</strong>
                <span>Reference Build</span>
              </Stat>

              <OutlineButton
                preserveSelectedDataset={false}
                to="/cohort/IPN/cohort"
              >
                Cohort Details
              </OutlineButton>
            </Stats>
          </HeroCopy>

          <About>
            <h3>About IPN</h3>

            <p>
              Idiopathic peripheral neuropathy (IPN) refers
              to neuropathy with no identifiable cause.
              This cohort combines genomic and phenotype
              data to facilitate discovery.
            </p>

            <Link
              preserveSelectedDataset={false}
              to="/cohort/IPN"
            >
              Learn more about IPN →
            </Link>
          </About>
        </Hero>

        {/* =================================================
            SHARED TABS
            ================================================= */}

        <Tabs>
          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN"
            $active={path === '/cohort/IPN'}
          >
            Overview
          </TabLink>

          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN/population"
            $active={path === '/cohort/IPN/population'}
          >
            Population Data
          </TabLink>

          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN/tandem-repeats"
            $active={path === '/cohort/IPN/tandem-repeats'}
          >
            Tandem Repeats
          </TabLink>

          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN/variants"
            $active={path === '/cohort/IPN/variants'}
          >
            SNVs / Small Variants
          </TabLink>

          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN/structural-variants"
            $active={path === '/cohort/IPN/structural-variants'}
          >
            Structural Variants
          </TabLink>

          <TabLink
            preserveSelectedDataset={false}
            to="/cohort/IPN/gene-region-search"
            $active={path === '/cohort/IPN/gene-region-search'}
          >
            Gene / Region Search
          </TabLink>

          <TabLink

            to="/cohort/IPN/downloads"
            $active={path === '/cohort/IPN/downloads'}
          >
            Downloads
          </TabLink>

          <TabLink

            to="/cohort/IPN/resources"
            $active={path === '/cohort/IPN/resources'}
          >
            Resources
          </TabLink>
        </Tabs>

        {/* =================================================
            PAGE-SPECIFIC CONTENT
            ================================================= */}

        {renderContent()}

      </Main>
    </Page>
  )
}

export default IPNPage